<script setup>
import { ref, reactive } from "vue";
import {
  getProjectById,
  postConfirmProject,
  postReturnProject,
} from "@/api/project";
import { queryStashList, queryStashById } from "@/api/stash";
import { ElMessage } from "element-plus";

// 事件
const emits = defineEmits(["afterSubmit"]);

// 仓库列表
const warehouseList = ref([]);

// 库位列表
const positionList = ref([]);

// 是否显示弹窗
const visible = ref(false);

// 是否显示驳回原因弹窗
const returnVisible = ref(false);

// 遮罩
const loading = ref(true);

// 提交按钮禁用
const buttonDisable = ref(false);

// 表单
const formData = reactive({});

// 描述列表字段配置
const descriptionColumns = ref([
  {
    label: "工程名称",
    prop: "projectName",
  },
  {
    label: "工程编号",
    prop: "projectNumber",
  },
  {
    label: "类型",
    prop: "type",
  },
  {
    label: "负责人",
    prop: "director",
  },
  {
    label: "联系方式",
    prop: "mobile",
  },
  {
    label: "审核状态",
    prop: "approvalStatus",
  },
  {
    label: "接收状态",
    prop: "status",
  },
]);

// 表格数据
const tableData = ref([]);

// 不予接收时的reason
const returnReason = ref(null);

// 弹窗打开
const dialogOpen = (id) => {
  buttonDisable.value = false;
  visible.value = true;
  getProjectInfoById(id);
  getWarehouseList();
};

// 根据id获取数据
const getProjectInfoById = (id) => {
  loading.value = true;
  getProjectById(id)
    .then((res) => {
      const keys = [
        "id",
        "projectName",
        "projectNumber",
        "type",
        "director",
        "mobile",
        "approvalStatus",
        "status",
        "auditTime",
      ];
      keys.forEach((key) => {
        formData[key] = res.data[key];
      });
      tableData.value = res.data.inventoryList || [];
    })
    .finally(() => {
      loading.value = false;
    });
};

// 获取仓库列表
const getWarehouseList = () => {
  queryStashList({ page: 1, limit: 999 }).then((res) => {
    warehouseList.value = res.data.records || [];
  });
};

// 根据仓库id获取库位列表
const getPositionListById = (id) => {
  queryStashById(id).then((res) => {
    positionList.value = res.data.warehousePositionList || [];
  });
};

// 仓库变化
const warehouseChanged = (row) => {
  row.positionId = null;
  getPositionListById(row.warehouseId);
};

// 点击接收或不接收
const formSubmit = (isReceive) => {
  if (!isReceive) {
    returnReason.value = null;
    return (returnVisible.value = true);
  }
  buttonDisable.value = true;
  const validateResult = validateTable();
  if (validateResult) {
    projectReceived();
  } else {
    buttonDisable.value = false;
  }
};

// 接收
const projectReceived = () => {
  postConfirmProject({
    ...formData,
    inventoryList: tableData.value,
    status: formData.status.value,
    type: formData.type.value,
    approvalStatus: formData.approvalStatus.value ?? null,
  })
    .then((res) => {
      if (res.code == "2000") {
        ElMessage.success("操作成功");
        visible.value = false;
        emits("afterSubmit");
      }
    })
    .catch(() => {
      buttonDisable.value = false;
    });
};

// 不予接收
const projectReturned = () => {
  if (!returnReason.value) return ElMessage.error("请填写不予接受原因");
  postReturnProject({
    id: formData.id,
    returnReason: returnReason.value,
  })
    .then((res) => {
      if (res.code == "2000") {
        ElMessage.success("操作成功");
        visible.value = false;
        emits("afterSubmit");
      }
    })
    .catch(() => {
      buttonDisable.value = false;
    });
};

// 校验表格中仓库和库位是否填充(只有接收操作才执行校验)
const validateTable = () => {
  let failCount = 0;
  tableData.value.forEach((item) => {
    if (!item.warehouseId || !item.positionId) {
      failCount++;
    }
  });

  if (failCount > 0) {
    ElMessage.error("仓库或库位不可为空");
    return false;
  }
  return true;
};

// 暴露函数
defineExpose({
  dialogOpen,
});
</script>

<template>
  <el-dialog
    title="工程材料详情"
    v-model="visible"
    :close-on-click-modal="false"
    width="60%"
  >
    <el-dialog
      title="不予接收"
      v-model="returnVisible"
      width="30%"
      :close-on-click-modal="false"
    >
      <el-form label-position="top">
        <el-form-item label="不接收原因：" :required="true">
          <el-input
            type="textarea"
            v-model="returnReason"
            :maxlength="150"
            show-word-limit
            :rows="3"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="returnVisible = false">取消</el-button>
          <el-button
            type="primary"
            @click="projectReturned()"
            :disabled="buttonDisable"
          >
            确定
          </el-button>
        </span>
      </template>
    </el-dialog>
    <div v-loading="loading">
      <div>
        <div class="title">基本信息</div>
        <el-descriptions :column="3" border>
          <template v-for="item in descriptionColumns" :key="item.prop">
            <el-descriptions-item :label="item.label" width="16%">
              <span
                v-if="['status', 'type', 'approvalStatus'].includes(item.prop)"
                >{{ formData[item.prop]?.desc || "-" }}</span
              >
              <span v-else>{{ formData[item.prop] }}</span>
            </el-descriptions-item>
          </template>
        </el-descriptions>
      </div>
      <div class="mt10">
        <div class="title">物料信息</div>
        <el-table
          :header-cell-style="{ 'text-align': 'center' }"
          :cell-style="{ 'text-align': 'center' }"
          :data="tableData"
        >
          <el-table-column label="物料名称" prop="materialName" />
          <el-table-column label="数量" prop="number" />
          <el-table-column label="仓库" prop="warehouseId">
            <template #default="{ row }">
              <el-select
                v-model="row.warehouseId"
                style="width: 100%"
                @change="warehouseChanged(row)"
                :disabled="formData.status?.value != '1'"
              >
                <el-option
                  v-for="item in warehouseList"
                  :key="item.id"
                  :label="item.name"
                  :value="item.id"
                />
              </el-select>
            </template>
          </el-table-column>
          <el-table-column label="库位" prop="positionId">
            <template #default="{ row }">
              <el-select
                v-model="row.positionId"
                style="width: 100%"
                :disabled="!row.warehouseId || formData.status?.value != '1'"
              >
                <el-option
                  v-for="item in positionList"
                  :key="item.id"
                  :label="item.name"
                  :value="item.id"
                />
              </el-select>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>
    <template #footer>
      <span class="dialog-footer">
        <el-button @click="visible = false">取消</el-button>
        <el-button
          v-if="formData.status?.value == '1'"
          type="primary"
          @click="formSubmit(true)"
          :disabled="buttonDisable"
          >接收</el-button
        >
        <el-button
          v-if="formData.status?.value == '1'"
          type="danger"
          @click="formSubmit(false)"
          :disabled="buttonDisable"
          >不予接收</el-button
        >
      </span>
    </template>
  </el-dialog>
</template>

<style scoped>
.title {
  border-left: 5px solid #409eff;
  font-size: 16px;
  font-weight: bold;
  padding-left: 10px;
  margin-bottom: 15px;
}

.mt10 {
  margin-top: 10px;
}
</style>
